import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { navForRole, SECTION_ORDER } from "@/lib/permissions";
import { ShieldAlert, ClipboardCheck, ChevronDown, Eye } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ValidationPanel } from "@/components/ValidationPanel";
import { cn } from "@/lib/utils";

const FOOTER = "RequiSure — Concept Prototype 0.2 | Developed by Inforno HealthTech | Synthetic Demonstration Environment | For Validation Only";

export function AppShell({ children }) {
  const { bootstrap, roleCode, setRole, persona, validationOpen, setValidationOpen, operatingMode, shadowLabel } = useApp();
  const location = useLocation();
  const nav = navForRole(roleCode);
  const personas = bootstrap?.personas || [];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50">
      {/* Header — navy is used selectively here as the single dark anchor */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-800 bg-slate-900 px-4 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-teal-500 font-display text-lg font-extrabold text-slate-900">R</div>
          <div className="leading-tight">
            <div className="font-display text-base font-bold">
              Requi<span className="text-teal-400">S</span>ure
            </div>
            <div className="text-[10px] text-slate-400">Laboratory Reimbursement Readiness Intelligence</div>
          </div>
          <span className="ml-2 rounded-md border border-amber-500/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300"
            data-testid="prototype-badge">
            Concept Prototype 0.2
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden text-right md:block">
            <div className="text-xs font-medium text-slate-200">{bootstrap?.organization?.name}</div>
            <div className="text-[10px] text-slate-400">Synthetic Demonstration Environment</div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" data-testid="role-switcher"
                className="h-9 gap-2 border border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700 hover:text-white">
                <div className="text-left leading-tight">
                  <div className="text-[9px] uppercase tracking-wide text-slate-400">Viewing As</div>
                  <div className="text-xs font-semibold">{persona?.role || "Select role"}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Viewing As (Persona)</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {personas.map((p) => (
                <DropdownMenuItem key={p.role_code}
                  data-testid={`role-option-${p.role_code}`}
                  onClick={() => setRole(p.role_code)}
                  className={cn("flex flex-col items-start gap-0.5", roleCode === p.role_code && "bg-teal-50")}>
                  <span className="text-sm font-semibold text-slate-900">{p.role}</span>
                  <span className="text-xs text-slate-500">{p.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" data-testid="validation-mode-toggle"
            onClick={() => setValidationOpen((v) => !v)}
            className={cn("gap-1.5", validationOpen ? "bg-teal-500 text-slate-900 hover:bg-teal-400" : "bg-slate-800 text-slate-100 hover:bg-slate-700")}>
            <ClipboardCheck className="h-4 w-4" /> Validation
          </Button>
        </div>
      </header>

      {operatingMode === "SHADOW" && (
        <div data-testid="shadow-mode-banner"
          className="flex shrink-0 items-center gap-2 border-b border-indigo-300 bg-indigo-50 px-4 py-1.5 text-xs font-semibold text-indigo-800">
          <Eye className="h-4 w-4" /> {shadowLabel || "SHADOW MODE — Recommendation recorded for validation only. Existing operational workflow remains authoritative."}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* Sidebar — light operational rail (navy no longer dominates the canvas) */}
        <aside className="flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white">
          <nav className="flex-1 space-y-4 overflow-y-auto p-3">
            {SECTION_ORDER.map((section) => {
              const items = nav.filter((n) => n.section === section);
              if (!items.length) return null;
              return (
                <div key={section}>
                  <div className="px-2 pb-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-400">{section}</div>
                  <div className="space-y-0.5">
                    {items.map((n) => {
                      const Icon = n.icon;
                      const active = n.path === "/" ? location.pathname === "/" : location.pathname.startsWith(n.path);
                      return (
                        <NavLink key={n.id} to={n.path} data-testid={`nav-${n.id}`}
                          className={cn(
                            "flex items-center gap-3 rounded-md border-l-2 py-2 pl-2.5 pr-3 text-sm font-medium transition-colors",
                            active
                              ? "border-teal-600 bg-teal-50 text-teal-800"
                              : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900")}>
                          <Icon className={cn("h-4 w-4", active ? "text-teal-600" : "text-slate-400")} /> {n.label}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
          <div className="border-t border-slate-200 p-3">
            <div className="flex items-center gap-1.5 text-[10px] leading-snug text-slate-400">
              <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              No real PHI — not authorized for PHI storage or processing.
            </div>
          </div>
        </aside>

        {/* Workspace */}
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1480px] p-6">{children}</div>
        </main>

        {validationOpen && <ValidationPanel screen={location.pathname} onClose={() => setValidationOpen(false)} />}
      </div>

      {/* Footer — light disclaimer bar */}
      <footer className="flex h-8 shrink-0 items-center justify-between border-t border-slate-200 bg-white px-4 font-mono text-[11px] text-slate-500"
        data-testid="app-footer">
        <span>{FOOTER}</span>
        <span className="hidden sm:inline text-slate-400">© Inforno HealthTech · Do not expose publicly</span>
      </footer>
    </div>
  );
}

export default AppShell;
